import Link from "next/link";
import { Modal, Button, Card } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import helper from "../../lib/helper";
import useSWR from "swr";
import Pagination from "../../components/Pagination";
import Heading from "../../components/Heading";
import Table from "../../components/Table";
import { useRouter } from "next/router";
import config from "../../lib/config";
import { useForm } from "react-hook-form";
import giftcardModel from "../../models/giftcard.model";
import CreatableSelect from "react-select/creatable";
import _ from "lodash";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Page = function () {
  const [showLoader, setShowLoader] = useState(false);
  const [show, setShow] = useState(false); // edit form
  
  const [showCreateForm, setShowCreateForm] = useState(false); // create form
 
  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);


  const router = useRouter();
  const queryParams = router.query;

  const {
    data: giftcardData,
    mutate: reloadGiftcardData,
    isLoading,
  } = useSWR(
    queryParams || null,
    () => giftcardModel.Giftcardlist(queryParams),
    config.swrConfig
  );

  // -------------------------------
  // Create form
  // -------------------------------
  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: errorsCreate },
    reset: resetCreate,
  } = useForm();

  const onSubmitCreateForm = handleSubmitCreate(async (data, event) => {
    const formData = new FormData(event.target);

    await giftcardModel
      .create(formData)
      .then((response) => {
        helper.sweetalert.toast(response.data?.message);
        reloadGiftcardData();
        setShowCreateForm(false);
        resetCreate();
      })
      .catch((error) => {
        helper.sweetalert.toast(error.response.data?.message, "error");
        _.each(error.response?.data?.errors, function (err, errorKey) {
          // Map backend errors to react-hook-form
        });
      });
  });

  // -------------------------------
  // Edit form
  // -------------------------------
  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    setValue: setValueEdit,
    watch: watchEdit,
    formState: { errors: errorsEdit },
    reset: resetEdit,
  } = useForm({
    defaultValues: {
      categories: [], // safe fallback
    },
  });

  const onSubmitEditForm = handleSubmitEdit(async (data, event) => {
    const formData = new FormData(event.target);

    await giftcardModel
      .update(data.id, formData)
      .then((response) => {
        helper.sweetalert.toast(response.data?.message);
        reloadGiftcardData();
        setShow(false);
        resetEdit();
      })
      .catch((error) => {
        helper.sweetalert.toast(error.response.data?.message, "error");
      });
  });

  const editCategory = async (
    id,
    categories,
    status,
    title,
    sku,
    primary_id,
    discount
  ) => {
    setValueEdit("id", id);
    setValueEdit(
      "categories[]",
      categories?.map((c) => ({ value: c, label: c })) || []
    );
    setValueEdit("status", status);
    setValueEdit("title", title);
    setValueEdit("sku", sku);
    setValueEdit("primary_id", primary_id);
    setValueEdit("discount", discount);
    setShow(true);
  };

  
  // -------------------------------
  // Columns
  // -------------------------------
  const columns = [
    { Header: "Title", accessor: "title" },
    { Header: "Product_SKU", accessor: "sku" },
    {
      Header: "Categories",
      accessor: "categories[]",
      Cell: ({ row }) =>
        row.original.categories?.length > 0 ? (
          row.original.categories?.map((c, i) => (
            <span key={i} className="badge bg-primary me-1">
              {c}
            </span>
          ))
        ) : (
          <span className="text-muted">No categories</span>
        ),
    },
    { Header: "Status", accessor: "status" },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <button
          className="btn btn-warning btn-sm"
          onClick={() =>
            editCategory(
              row.original.id,
              row.original.categories,
              row.original.status,
              row.original.title,
              row.original.sku,
              row.original.primary_id,
              row.original.discount
            )
          }
        >
          <i className="bi bi-pencil"></i>
        </button>
      ),
    },
  ];

  return (
    <>
      <Heading title="Giftcards" showLoader={isLoading || showLoader}>
        <Button onClick={() => setShowCreateForm(true)} className="btn btn-sm">
          Create
        </Button>
        
      </Heading>
      

      {/* ---------------- CREATE FORM ---------------- */}
      <Modal show={showCreateForm} onHide={() => setShowCreateForm(false)}>
        <form onSubmit={onSubmitCreateForm}>
          <Card>
            <Card.Header>Create Giftcard</Card.Header>
            <Card.Body>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsCreate?.title && "is-invalid"
                  }`}
                  {...registerCreate("title", {
                    required: "Title is required",
                  })}
                />
                <div className="invalid-feedback">
                  {errorsCreate.title?.message}
                </div>
              </div>

              <div className="form-group mt-3">
                <label>SKU</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsCreate?.sku && "is-invalid"
                  }`}
                  {...registerCreate("sku", { required: "SKU is required" })}
                />
                <div className="invalid-feedback">
                  {errorsCreate.sku?.message}
                </div>
              </div>

              <div className="form-group mt-3">
                <label>PID</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsCreate?.primary_id && "is-invalid"
                  }`}
                  {...registerCreate("primary_id", {
                    required: "primary_id is required",
                  })}
                />
                <div className="invalid-feedback">
                  {errorsCreate.primary_id?.message}
                </div>
                <div className="form-group mt-3">
                  <label>Discount</label>
                  <input
                    type="text"
                    className={`form-control ${
                      errorsCreate?.discount && "is-invalid"
                    }`}
                    {...registerCreate("discount", {
                      required: "Discount is required",
                    })}
                  />
                  <div className="invalid-feedback">
                    {errorsCreate.discount?.message}
                  </div>
                  <input
                    type="hidden"
                    value={"pending"}
                    {...registerCreate("status")}
                  />
                </div>
                <div className="form-group mt-3">
                  <label>Categories</label>
                  <CreatableSelect name="categories[]" isMulti />

                  <div className="invalid-feedback">
                    {errorsCreate.categories?.message}
                  </div>
                </div>
              </div>
            </Card.Body>
            <Card.Footer>
              <button type="submit" className="btn btn-primary float-end">
                Save
              </button>
            </Card.Footer>
          </Card>
        </form>
      </Modal>

      {/* ---------------- EDIT FORM ---------------- */}
      <Modal show={show} onHide={() => setShow(false)}>
        <form onSubmit={onSubmitEditForm}>
          <Card>
            <Card.Header>Edit Giftcard</Card.Header>
            <Card.Body>
              <div className="form-group mt-3">
                <label>Title</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsEdit?.title && "is-invalid"
                  }`}
                  {...registerEdit("title", { required: "Title is required" })}
                />

                <div className="invalid-feedback">
                  {errorsEdit.title?.message}
                </div>
              </div>
              <div className="form-group mt-3">
                <label>SKU</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsEdit?.sku ? "is-invalid" : ""
                  }`}
                  {...registerEdit("sku", { required: "Sku is required" })}
                />
                <div className="invalid-feedback">
                  {errorsEdit.SKU?.message}
                </div>
              </div>
              <div className="form-group mt-3">
                {}
                <label>Discount</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsEdit?.discount ? "is-invalid" : ""
                  }`}
                  {...registerEdit("discount")}
                />
                <div className="invalid-feedback">
                  {errorsEdit.discount?.message}
                </div>
              </div>
              <div className="form-group mt-3">
                <label>PID</label>
                <input
                  type="text"
                  className={`form-control ${
                    errorsEdit?.primary_id ? "is-invalid" : ""
                  }`}
                  {...registerEdit("primary_id", {
                    required: "primary_id is required",
                  })}
                />
                <div className="invalid-feedback">
                  {errorsEdit.primary_id?.message}
                </div>
              </div>

              <div className="form-group">
                <label>Categories</label>
                <CreatableSelect
                  isMulti
                  value={watchEdit("categories[]") || []}
                  onChange={(val) => setValueEdit("categories[]", val)}
                  name="categories[]"
                />
              </div>

              <div className="form-group mt-3">
                <label>Status</label>
                <select
                  className={`form-select ${
                    errorsEdit?.status && "is-invalid"
                  }`}
                  {...registerEdit("status", { required: "Status required" })}
                >
                  <option value="">Select</option>
                  <option value="pending">Pending</option>
                  <option value="publish">Publish</option>
                </select>
                <div className="invalid-feedback">
                  {errorsEdit.status?.message}
                </div>
              </div>
            </Card.Body>
            <Card.Footer>
              <button type="submit" className="btn btn-primary float-end">
                Save
              </button>
            </Card.Footer>
          </Card>
        </form>
      </Modal>

      {/* ---------------- LISTING ---------------- */}
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">Giftcards</div>
          <div className="table-responsive">
            <Table columns={columns} data={giftcardData?.data || []} />
          </div>
          <div className="card-footer table-responsive">
            <Pagination meta={giftcardData?.meta} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;
